import {useState} from "react"
import AppLayout from "@/components/AppLayout"
import CreatePageIcon from "@material-ui/icons/Add"
import GoogleBizIcon from "@material-ui/icons/Store"
import FacebookIcon from "@material-ui/icons/Facebook"
import DeleteLeadPageIcon from "@material-ui/icons/Delete"
import EditPageIcon from "@material-ui/icons/Edit"
import type {LeadPageType} from "@/lib/server/models/leadPage"
import {trpc} from "@/lib/requests/index"
import {safeIO} from "@/lib/validation/io"
import {LoadingOverlay} from "@/components/loading"
import LeadPageQrCodeIcon from "@material-ui/icons/CropFree"
import {qrcode, staticResources} from "@/lib/consts"
import {useRouter} from "next/router"
import type {LeadpageInit} from "@/lib/server/trpcRouters/leadpages"
import ViewPageIcon from "@material-ui/icons/Visibility"

const icons = {
  googleMyBusiness: () => <GoogleBizIcon/>,
  yelp: () => <img
    className="inline-block"
    src="/leadPages/yelp-logo.svg"
    width="15px"
    height="15px"
  />,
  facebook: () => <FacebookIcon/>,
  zillow: () => <img
    className="inline-block"
    src="/logos/zillow_transparent.png"
    width="15px"
    height="15px"
  />,
} as const

export default function LeadPages(): JSX.Element {
  const trpcClient = trpc.useContext()
  const leadpages = trpc.useQuery([
    "leadpages.list", {offset: 0}
  ])
  const {refetch: fetchContact} = trpc.useQuery(["user.contact"], {
    enabled: false
  })
  
  const initPage = trpc.useMutation("leadpages.init", {
    onSuccess: (newLeadpage) => trpcClient.setQueryData(
      ["leadpages.list", {offset: 0}], 
      (oldPages) => {
        const old = oldPages?.pages || []
        return {
          pages: [newLeadpage, ...old],
          finished: oldPages?.finished || true
        }
    }),
    onError: () => window.alert("An error occurred when creating page!"),
    onSettled: () => setCreatingPage(false)
  })

  const deletePage = trpc.useMutation("leadpages.delete", {
    onSuccess: (id) => trpcClient.setQueryData(
      ["leadpages.list", {offset: 0}],
      (oldPages) => {
        const pages = oldPages?.pages || []
        const finished = oldPages?.finished || true 
        const newData = [...pages]
        const index = newData.findIndex((page) => page._id === id)
        newData.splice(index, 1)
        return {pages: newData, finished} 
      }
    ),
    onError: (_, variables) => console.warn("couldn't delete page with vars =", variables)
  })

  const [showPageLayouts, setShowPageLayouts] = useState(false)
  const [creatingPage, setCreatingPage] = useState(false)

  function initLeadPage(
    name: string, 
    type: LeadPageType, 
    config: LeadpageInit
  ) {
    if (!window.confirm(`Are you sure you want to create a ${name} lead page?`)) {
      return
    }
    setCreatingPage(true)
    setShowPageLayouts(false)
    initPage.mutate({type, ...config})
  }

  async function generateQrCode(leadPage: LeadPageCore) {
    const pageId = leadPage._id, pageName = leadPage.title
    const leadPageUrl = `${window.location.origin}/googleMyBusiness/${pageId}`
    const qrlib = await import("qrcode")
    const dataUrl = await qrlib.default.toDataURL(
      leadPageUrl, qrcode
    )
    const download = document.createElement("a")
    download.href = dataUrl
    download.download = `qr-leadpage-${pageName}.png`
    document.body.appendChild(download)
    download.click()
  }

  async function deleteLeadPage(id: string) {
    deletePage.mutate({pageId: id})
  }

  const router = useRouter()
  function editLeadPage(leadPage: LeadPageCore) {
    router.push(`/leadPages/${leadPage._id}`)
  }

  async function createPage(name: string, type: LeadPageType) {
    const res = await safeIO(fetchContact)
    if (!res.success || !res.data) {
      window.alert("An error occured when creating lead page")
      return
    }
    const {data} = res
    const {
      firstName = "", lastName = "", city = "Calgary",
      state = "AB", zip = "015677", 
      address = "321 Nolancrest Manor NW",
      notificationPhoneNumbers = [],
      company = "eXp Realty"
    } = data.data?.contact || {}
    
    let cityAndState = ""
    if (city.length > 0 && state.length > 0) {
      cityAndState = `${city}, ${state}`
    }
    
    let phone = "+14031231234"
    if (notificationPhoneNumbers.length > 0) {
      phone = notificationPhoneNumbers[0]
    }

    const config = {
      firstName, lastName, cityAndState,
      completeAddress: `${address}, ${cityAndState}, ${zip}`,
      phone, realtyHouse: company,
      professionalTitle: "", 
      pictureUrl: staticResources.favicon
    }
    initLeadPage(name, type, config)
  }

  const webSubdominQuery = trpc.useQuery([
    "webLeadMagnet.onehomesSubdomain"
  ])
  const subdomainMeta = webSubdominQuery.data || null

  return <AppLayout requiredAuth="user">
      <section className="flex flex-col items-center w-full">
        <div className="flex flex-col items-center my-6">
          <h1 className="text-3xl sm:text-4xl font-display">
            {"Lead Pages"}
          </h1>
        </div>

        {!leadpages.isLoading ? <div className="my-2">
          <button onClick={() => setShowPageLayouts(!showPageLayouts)}>
            {showPageLayouts ? <span className="text-yellow-600 hover:text-red-600">
              <CreatePageIcon />
              {"Hide Templates"}
            </span> : <span className="text-blue-600 hover:text-green-600">
              <CreatePageIcon />
              {"Page Templates"}
            </span>}
          </button>
        </div> : null}

        {creatingPage ? <>
          <div className="my-2 w-4/5 text-sm animate-pulse text-gray-400">
            {"Creating page..."}
          </div>
        </> : <></>}

        {showPageLayouts ? <>
          <div className="mb-5">
            <button 
              className="bg-gray-200 p-2 rounded shadow text-gray-900 hover:bg-green-600"
              onClick={() => { createPage("Google My Business", "googleMyBusiness") }}
            >
              <GoogleBizIcon className="mr-1 text-blue-600"/>
              {"Google"}
            </button>

            <button 
              className="bg-gray-200 p-2 mx-2 rounded shadow text-gray-900 hover:bg-green-600"
              onClick={() => { createPage("Yelp", "yelp") }}
            >
              <span style={{marginRight: "0.375rem"}}>
                <icons.yelp/>
              </span>
              {"Yelp"}
            </button>

            <button 
              className="bg-gray-200 p-2 mx-2 rounded shadow text-gray-900 hover:bg-green-600"
              onClick={() => { createPage("Facebook", "facebook") }}
            >
              <FacebookIcon className="mr-1"/>
              {"Facebook"}
            </button>

            <button 
              className="bg-gray-200 p-2 mx-2 rounded shadow text-gray-900 hover:bg-green-600"
              onClick={() => { createPage("Zillow", "zillow") }}
            >
              <span className="mr-1">
                <icons.zillow/>
              </span>
              {"Zillow"}
            </button>
          </div>
        </> : <></>}
        
        <div>
          <LoadingOverlay loading={leadpages.isLoading}>
            {leadpages.isError || !leadpages.data ? <>
              <div className="w-full text-center text-red-600">
                {"An error occurred when fetching lead pages"}
              </div>
            </> : <>
              <div className="overflow-y-scroll">
                
                {subdomainMeta ? <div
                  className="hover:bg-gray-300 cursor-pointer flex items-center w-full shadow bg-gray-200 p-4 rounded mb-1 text-gray-900"
                >
                  <div className="text-sm w-1/3">
                    {"CMA Requests"}
                  </div>

                  <div className="w-2/3 text-center text-blue-600">
                    <span title="Onehomes Website">
                      <img
                        className="inline-block"
                        src="/apps/bulletproof-cma.png"
                        width="15px"
                        height="15px"
                      />
                    </span>

                    <span className="text-green-600 ml-2 text-sm">
                      {`0 leads`}
                    </span>

                    {subdomainMeta.createdAt ? <span 
                      className="ml-2 text-purple-600 text-xs"
                    >
                        {new Date(subdomainMeta.createdAt).toLocaleString("en-us", {month: "2-digit", year: "numeric"})}
                    </span> : null}
                    
                  </div>

                  <div className="w-1/3 text-center">
                    <a 
                      href={`${window.location.protocol}//${subdomainMeta.subdomain}.${subdomainMeta.oneHomesOrigin}/cma`} 
                      target="_blank"
                    >
                      <button 
                        className="ml-1 text-purple-600 hover:bg-gray-400 p-1 rounded-full" 
                        title="Edit"
                      >
                        <ViewPageIcon/>
                      </button>
                    </a>
                  </div>
                </div> : null}

                {leadpages.data.pages.map((page) => <LeadPageSummary 
                    key={`leadPage-${page._id}`}
                    {...page as LeadPageCore}
                    onEdit={editLeadPage}
                    onGenerateQrCode={generateQrCode}
                    onDelete={deleteLeadPage}
                  />)}
              </div>
            </>}
          </LoadingOverlay>
        </div>
      </section>
  </AppLayout>
}


interface LeadPageCore {
  title: string
  type: LeadPageType
  leadCount: number
  createdAt: Date
  _id: string
}

interface LeadPageSummaryProps extends LeadPageCore {
  className?: string
  onGenerateQrCode: (leadPage: LeadPageCore) => unknown
  onEdit: (leadPage: LeadPageCore) => unknown
  onDelete: (id: string) => unknown
}

function LeadPageSummary(props: LeadPageSummaryProps): JSX.Element {
  const dateString = new Date(props.createdAt).toLocaleString(
    "en-us", {month: "2-digit", year: "numeric"}
  )

  let logoTooltip = ""
  if (props.type === "googleMyBusiness") {
    logoTooltip = "Google"
  } else {
    logoTooltip = props.type[0].toUpperCase() + props.type.slice(1)
  }

  return <div 
    className="hover:bg-gray-300 cursor-pointer flex items-center w-full shadow bg-gray-200 p-4 rounded mb-1 text-gray-900"
  >
    <div className="text-sm w-1/3">
      {props.title}
    </div>

    <div className="w-2/3 text-center text-blue-600">
      <span title={logoTooltip} >
        {icons[props.type as LeadPageType]()}
      </span>

      <span className="text-green-600 ml-2 text-sm">
        {`${props.leadCount} leads`}
      </span>

      <span className="ml-2 text-purple-600 text-xs">
          {dateString}
      </span>
    </div>

    <div className="w-1/3 text-center">
      <button 
        className="ml-1 text-green-600 hover:bg-gray-400 p-1 rounded-full" 
        title="Edit"
        onClick={() => { props.onEdit(props) }}
      >
        <EditPageIcon/>
      </button>

      <button 
        className="ml-1 text-blue-600 hover:bg-gray-400 p-1 rounded-full" 
        title="Download QR Code"
        onClick={() => { props.onGenerateQrCode(props) }}
      >
        <LeadPageQrCodeIcon />
      </button>

      <button 
        className="ml-1 text-red-600 hover:bg-gray-400 p-1 rounded-full" 
        title="Delete"
        onClick={() => {
          if (!window.confirm(`Are you sure you want to delete ${props.title}?`)) {
            return
          }
          props.onDelete(props._id)
        }}
      >
        <DeleteLeadPageIcon />
      </button>
    </div>
  </div>
}